import React, { type ReactNode } from "react";

/** A small React-only markdown renderer. Raw HTML is always text; links are allowlisted. */
function inline(text: string): ReactNode[] {
  const pattern = /\[([^\]\n]+)\]\(([^\s)]+)\)|\*\*([^*\n]+)\*\*|`([^`\n]+)`|\*([^*\n]+)\*/g;
  const parts: ReactNode[] = [];
  let previous = 0;
  for (const match of text.matchAll(pattern)) {
    parts.push(text.slice(previous, match.index));
    const key = match.index;
    if (match[1]) {
      const href = match[2];
      const safe = /^(https?:\/\/|\/(?!\/)|#)/i.test(href) && !Array.from(href).some((character) => character.charCodeAt(0) <= 32 || character.charCodeAt(0) === 92);
      parts.push(safe ? <a key={key} href={href} className="break-words font-medium text-teal-800 underline decoration-teal-300 underline-offset-2 hover:decoration-teal-800" target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">{match[1]}</a> : <span key={key}>{match[1]}</span>);
    } else if (match[3]) parts.push(<strong key={key} className="font-semibold text-stone-900">{match[3]}</strong>);
    else if (match[4]) parts.push(<code key={key} className="rounded bg-stone-100 px-1 py-0.5 text-[0.9em]">{match[4]}</code>);
    else parts.push(<em key={key}>{match[5]}</em>);
    previous = match.index! + match[0].length;
  }
  parts.push(text.slice(previous));
  return parts;
}

export function SafeMarkdown({ text }: { text: string }) {
  const lines = text.split(/\r?\n/);
  const blocks: ReactNode[] = [];
  for (let i = 0; i < lines.length;) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    const key = i;
    const heading = /^(#{1,6})\s+(.+)$/.exec(line);
    if (heading) {
      blocks.push(heading[1].length <= 2 ? <h2 key={key} className="pt-2 text-lg font-semibold leading-7 text-stone-900">{inline(heading[2])}</h2> : <h3 key={key} className="pt-1 font-semibold text-stone-900">{inline(heading[2])}</h3>);
      i++; continue;
    }
    if (/^\s*([-*_])(?:\s*\1){2,}\s*$/.test(line)) { blocks.push(<hr key={key} className="border-stone-200" />); i++; continue; }
    if (/^```/.test(line)) {
      const code: string[] = []; i++;
      while (i < lines.length && !/^```/.test(lines[i])) code.push(lines[i++]);
      if (i < lines.length) i++;
      blocks.push(<pre key={key} className="overflow-x-auto rounded-lg bg-stone-50 p-4 text-xs"><code>{code.join("\n")}</code></pre>); continue;
    }
    if (/^\s*(?:[-*+] |\d+\. )/.test(line)) {
      const ordered = /^\s*\d+\. /.test(line);
      const items: ReactNode[] = [];
      const pattern = ordered ? /^\s*\d+\.\s+/ : /^\s*[-*+]\s+/;
      while (i < lines.length && pattern.test(lines[i])) items.push(<li key={i}>{inline(lines[i++].replace(pattern, ""))}</li>);
      blocks.push(ordered ? <ol key={key} className="list-decimal space-y-2 pl-5">{items}</ol> : <ul key={key} className="list-disc space-y-2 pl-5 marker:text-teal-700">{items}</ul>); continue;
    }
    if (line.includes("|") && i + 1 < lines.length && /^\s*\|?\s*:?-{3,}/.test(lines[i + 1])) {
      const cells = (row: string) => row.trim().replace(/^\||\|$/g, "").split("|").map((cell) => cell.trim());
      const headers = cells(line); i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes("|")) rows.push(cells(lines[i++]));
      blocks.push(<div key={key} className="overflow-x-auto rounded-lg border border-stone-200"><table className="w-full text-left text-sm"><thead className="bg-stone-50"><tr>{headers.map((cell, index) => <th key={index} scope="col" className="px-4 py-3 font-semibold text-stone-900">{inline(cell)}</th>)}</tr></thead><tbody className="divide-y divide-stone-200">{rows.map((row, index) => <tr key={index}>{row.map((cell, column) => <td key={column} className="px-4 py-3 align-top">{inline(cell)}</td>)}</tr>)}</tbody></table></div>); continue;
    }
    const paragraph: string[] = [line]; i++;
    while (i < lines.length && lines[i].trim() && !/^(#{1,6}\s|```|\s*(?:[-*+] |\d+\. ))/.test(lines[i])) paragraph.push(lines[i++]);
    blocks.push(<p key={key} className="whitespace-pre-wrap">{inline(paragraph.join("\n"))}</p>);
  }
  return <div className="space-y-4 break-words text-sm leading-7 text-stone-700">{blocks}</div>;
}
